import { Controller } from "@hotwired/stimulus"

// Ripple dot grid that pulses outward from the viewport center.
// Each dot's sine-wave phase is offset by its distance from center,
// so brightness radiates outward in expanding rings — like a ripple.
//
// Performance notes:
//   - Element metrics (offsetTop, offsetHeight) are cached and only updated
//     on resize — never read mid-frame to avoid synchronous layout flushes.
//   - Dots are batched into alpha buckets so we issue ~16 fill() calls per
//     frame instead of one per dot (~1440), cutting GPU round-trips by ~90×.
//   - Canvas is viewport-sized (not full-page) and uses transform:translateY
//     to track scroll — efficient GPU-composited repaint, no layout reflow.
//   - will-change:transform promotes the canvas to its own compositor layer.
export default class extends Controller {
  connect() {
    this.canvas = document.createElement('canvas')
    this.canvas.style.cssText =
      'position:absolute;top:0;left:0;pointer-events:none;z-index:-1;will-change:transform;'
    this.element.prepend(this.canvas)
    this.ctx = this.canvas.getContext('2d')
    this.startTime = performance.now()

    // Cached element metrics — updated on resize/turbo:load, never read per-frame
    this._elTop    = 0
    this._elHeight = 0

    this.boundResize    = this._onResize.bind(this)
    // Re-measure after Turbo navigation completes — content is fully laid out by then
    this.boundTurboLoad = () => requestAnimationFrame(() => this._updateMetrics())

    window.addEventListener('resize', this.boundResize)
    document.addEventListener('turbo:load', this.boundTurboLoad)

    this.resizeObserver = new ResizeObserver(() => {
      this._updateMetrics()
    })
    this.resizeObserver.observe(this.element)

    // Defer initial metrics + canvas setup until after first paint so offsetHeight is accurate
    requestAnimationFrame(() => {
      this._updateMetrics()
      this._setCanvasSize()
      this.raf = requestAnimationFrame(this.tick.bind(this))
    })
  }

  disconnect() {
    cancelAnimationFrame(this.raf)
    window.removeEventListener('resize', this.boundResize)
    document.removeEventListener('turbo:load', this.boundTurboLoad)
    
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    
    this.canvas.remove()
  }

  _updateMetrics() {
    // Compute once: element's top offset from document top and its height.
    // getBoundingClientRect + scrollY gives the stable document-top offset.
    const rect      = this.element.getBoundingClientRect()
    this._elTop     = rect.top + window.scrollY
    this._elHeight  = this.element.offsetHeight
  }

  _onResize() {
    this._updateMetrics()
    this._setCanvasSize()
  }

  _setCanvasSize() {
    this.canvas.width  = window.innerWidth
    this.canvas.height = window.innerHeight
  }

  tick(now) {
    const t   = (now - this.startTime) / 1000
    const ctx = this.ctx
    const W   = this.canvas.width
    const H   = this.canvas.height

    // Translate canvas to the visible strip of the element.
    // Uses cached metrics — no getBoundingClientRect() here.
    const offsetY   = Math.max(0, window.scrollY - this._elTop)
    const maxOffset = Math.max(0, this._elHeight - H)
    this.canvas.style.transform = `translateY(${Math.min(offsetY, maxOffset)}px)`

    const cx = W / 2
    const cy = H / 2
    const spacing   = 30
    const waveFreq  = 0.014
    const waveSpeed = 1.8
    const minAlpha  = 0.12
    const maxAlpha  = 0.48
    const minRadius = 0.8
    const maxRadius = 1.8

    ctx.clearRect(0, 0, W, H)

    // --- Alpha-bucketed batch drawing ---
    // Group dots by quantized wave value → 16 buckets → 16 fill() calls/frame
    // instead of one per dot. Cuts GPU draw calls by ~90×.
    const BUCKETS = 16
    // Reuse flat typed arrays to avoid per-frame GC pressure (x, y, r triples)
    if (!this._buckets) {
      this._buckets = Array.from({ length: BUCKETS }, () => new Float32Array(3000))
      this._bLen    = new Int32Array(BUCKETS)
    }
    const buckets = this._buckets
    const bLen    = this._bLen
    bLen.fill(0)

    for (let x = spacing / 2; x < W; x += spacing) {
      for (let y = spacing / 2; y < H; y += spacing) {
        const dx   = x - cx
        const dy   = y - cy
        const dist = Math.sqrt(dx * dx + dy * dy)
        const wave   = 0.5 + 0.5 * Math.sin(t * waveSpeed - dist * waveFreq)
        const radius = minRadius + (maxRadius - minRadius) * wave
        const bi     = Math.min(BUCKETS - 1, (wave * BUCKETS) | 0)
        const i      = bLen[bi]
        buckets[bi][i]     = x
        buckets[bi][i + 1] = y
        buckets[bi][i + 2] = radius
        bLen[bi] += 3
      }
    }

    for (let bi = 0; bi < BUCKETS; bi++) {
      const len = bLen[bi]
      if (len === 0) continue
      const dots  = buckets[bi]
      const wave  = (bi + 0.5) / BUCKETS
      const alpha = minAlpha + (maxAlpha - minAlpha) * wave
      ctx.beginPath()
      for (let i = 0; i < len; i += 3) {
        const r = dots[i + 2]
        ctx.moveTo(dots[i] + r, dots[i + 1])
        ctx.arc(dots[i], dots[i + 1], r, 0, Math.PI * 2)
      }
      ctx.fillStyle = `rgba(96,165,250,${alpha.toFixed(3)})`
      ctx.fill()
    }

    this.raf = requestAnimationFrame(this.tick.bind(this))
  }
}
